import { useRef, useState, type ChangeEvent } from "react";
import type { AllowedSize } from "../../utils/imageValidator";
import { validateImageFileMeta, validateImageDimension } from "../../utils/imageValidator";
import MomentImageLibraryModal from "../MomentImageLibraryModal";

type Mode = "upload" | "library";

type Props = {
  imageName: string | null;
  sizeHint: string;
  allowedSizes: AllowedSize[];
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onLibrarySelect?: (imgUrl: string) => void;
  compact?: boolean;
};

export default function ImagePickerArea({
  imageName, sizeHint, allowedSizes,
  onFileChange, onLibrarySelect,
  compact = false,
}: Props) {
  const [mode, setMode] = useState<Mode>("upload");
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasLibrary = !!onLibrarySelect;
  const padding = compact ? "tw-py-3" : "tw-py-5";
  const iconSize = compact ? "tw-text-base" : "tw-text-2xl";
  const textSize = compact ? "tw-text-xs" : "tw-text-sm";

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const metaError = validateImageFileMeta(file);
    if (metaError) { setError(metaError); e.target.value = ""; return; }
    const dimError = await validateImageDimension(file, allowedSizes);
    if (dimError) { setError(dimError); e.target.value = ""; return; }
    setError(null);
    onFileChange(e);
  };

  const handleLibrarySelect = (imgUrl: string) => {
    setShowModal(false);
    setError(null);
    onLibrarySelect?.(imgUrl);
  };

  const handleUploadClick = () => {
    setMode("upload");
    inputRef.current?.click();
  };

  const handleLibraryClick = () => {
    setMode("library");
    setShowModal(true);
  };

  const handleBodyClick = () => {
    if (mode === "library") setShowModal(true);
    else inputRef.current?.click();
  };

  const bodyLabel = imageName
    ? imageName
    : mode === "library"
      ? "업로드된 이미지에서 선택하세요."
      : "이미지를 등록하세요.";

  return (
    <div className="tw-space-y-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="tw-hidden"
        onChange={handleFileChange}
      />

      <div className="tw-rounded-xl tw-overflow-hidden" style={{ border: "1px solid #e5e7eb" }}>

        {/* 액션 탭 행 — div 사용으로 Bootstrap button 스타일 간섭 차단 */}
        {hasLibrary && (
          <div className="tw-flex" style={{ borderBottom: "1px solid #e5e7eb" }}>
            <div
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(e) => e.key === "Enter" && handleUploadClick()}
              className={`tw-flex-1 tw-py-2 tw-text-xs tw-font-medium tw-text-center tw-cursor-pointer tw-select-none tw-transition-colors ${
                mode === "upload"
                  ? "tw-bg-white tw-text-gray-800"
                  : "tw-bg-gray-50 tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100"
              }`}
            >
              이미지 등록하기
            </div>
            <div style={{ width: "1px", backgroundColor: "#e5e7eb", flexShrink: 0 }} />
            <div
              role="button"
              tabIndex={0}
              onClick={handleLibraryClick}
              onKeyDown={(e) => e.key === "Enter" && handleLibraryClick()}
              className={`tw-flex-1 tw-py-2 tw-text-xs tw-font-medium tw-text-center tw-cursor-pointer tw-select-none tw-transition-colors ${
                mode === "library"
                  ? "tw-bg-blue-50 tw-text-blue-700"
                  : "tw-bg-gray-50 tw-text-gray-400 hover:tw-text-blue-500 hover:tw-bg-blue-50"
              }`}
            >
              이미지 불러오기
            </div>
          </div>
        )}

        {/* 상태 표시 영역 */}
        <div
          role="button"
          tabIndex={0}
          onClick={hasLibrary ? handleBodyClick : () => inputRef.current?.click()}
          onKeyDown={(e) => e.key === "Enter" && (hasLibrary ? handleBodyClick() : inputRef.current?.click())}
          className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1.5 ${padding} tw-cursor-pointer tw-select-none tw-transition-colors ${
            mode === "library" && hasLibrary
              ? "tw-bg-blue-50 hover:tw-bg-blue-100"
              : "tw-bg-white hover:tw-bg-gray-50"
          }`}
        >
          <span className={`${iconSize} ${mode === "library" && hasLibrary ? "tw-text-blue-300" : "tw-text-gray-300"}`}>
            🖼
          </span>
          <span className={`${textSize} tw-text-center tw-break-all ${mode === "library" && hasLibrary ? "tw-text-blue-600 tw-font-medium" : "tw-text-gray-500"}`}>
            {bodyLabel}
          </span>
          <span className="tw-text-xs tw-text-rose-400">
            {sizeHint} · JPG/JPEG/PNG · 10MB 이하
          </span>
        </div>
      </div>

      {error && <p className="tw-text-xs tw-text-red-500">{error}</p>}

      {hasLibrary && (
        <MomentImageLibraryModal
          open={showModal}
          allowedSizes={allowedSizes}
          onSelect={(item) => handleLibrarySelect(item.imgUrl)}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
